import {Button, Group, Modal, Stack, Textarea, TextInput} from '@mantine/core'
import {useForm} from '@mantine/form'
import {useEffect, useState} from 'react'

import {formatPhoneInput} from 'src/lib/format-phone'

import type {CreateCustomerInput, Customer} from './types'

type CustomerFormValues = {
    name: string
    slug: string
    contactName: string
    title: string
    description: string
    phone: string
    email: string
}

type CustomerFormModalProps = {
    opened: boolean
    mode: 'create' | 'edit'
    initialName?: string
    customer?: Customer
    onClose: () => void
    onCreate: (input: CreateCustomerInput) => Promise<Customer>
    onUpdate: (id: string, input: CreateCustomerInput) => Promise<Customer>
    onSaved: (customer: Customer) => void
}

function customerToFormValues(customer: Customer): CustomerFormValues {
    return {
        name: customer.name,
        slug: customer.slug ?? '',
        contactName: customer.contactName,
        phone: customer.phone,
        email: customer.email ?? '',
    }
}

export function CustomerFormModal({
    opened,
    mode,
    initialName = '',
    customer,
    onClose,
    onCreate,
    onUpdate,
    onSaved,
}: CustomerFormModalProps) {
    const [isSubmitting, setIsSubmitting] = useState(false)
    const isEdit = mode === 'edit'

    const form = useForm<CustomerFormValues>({
        initialValues: {
            name: initialName,
            slug: '',
            contactName: '',
            title: '',
            description: '',
            phone: '',
            email: '',
        },
        validate: {
            name: (value) => (value.trim() ? null : 'Customer name is required'),
            slug: (value) =>
                value.trim() && !/^[A-Z]{4}$/.test(value.trim())
                    ? 'Slug must be exactly 4 uppercase letters'
                    : null,
            contactName: (value) => (value.trim() ? null : 'Contact name is required'),
            phone: (value) => (value.trim() ? null : 'Phone number is required'),
            email: (value) =>
                value.trim() && !/^\S+@\S+\.\S+$/.test(value) ? 'Invalid email address' : null,
        },
    })

    useEffect(() => {
        if (!opened) {
            return
        }
        if (isEdit && customer) {
            form.setValues(customerToFormValues(customer))
        } else {
            form.setValues({
                name: initialName,
                slug: '',
                contactName: '',
                title: '',
                description: '',
                phone: '',
                email: '',
            })
        }
        form.clearErrors()
        // eslint-disable-next-line react-hooks/exhaustive-deps -- reset modal fields when opened
    }, [opened, isEdit, customer, initialName])

    function handleClose() {
        form.reset()
        onClose()
    }

    async function handleSubmit(values: CustomerFormValues) {
        const input: CreateCustomerInput = {
            name: values.name.trim(),
            slug: values.slug.trim().toUpperCase() || undefined,
            contactName: values.contactName.trim(),
            title: values.title.trim() || undefined,
            description: values.description.trim() || undefined,
            phone: values.phone.trim(),
            email: values.email.trim() || undefined,
        }

        setIsSubmitting(true)
        try {
            const saved =
                isEdit && customer ? await onUpdate(customer.id, input) : await onCreate(input)
            onSaved(saved)
            form.reset()
            onClose()
        } finally {
            setIsSubmitting(false)
        }
    }

    return (
        <Modal
            opened={opened}
            onClose={handleClose}
            title={isEdit ? 'Edit customer' : 'Create new customer'}
            centered
        >
            <form onSubmit={form.onSubmit(handleSubmit)}>
                <Stack gap="md">
                    <TextInput label="Customer name" {...form.getInputProps('name')} />
                    <TextInput
                        label="Customer slug (4 uppercase letters)"
                        description="Optional 4-letter code for job IDs. Auto-generated if left blank."
                        maxLength={4}
                        {...form.getInputProps('slug')}
                    />
                    <TextInput
                        label="Contact name"
                        withAsterisk
                        {...form.getInputProps('contactName')}
                    />
                    <TextInput label="Title" {...form.getInputProps('title')} />
                    <Textarea
                        label="Description"
                        description='Internal note (e.g. "Contact for billing")'
                        minRows={2}
                        {...form.getInputProps('description')}
                    />
                    <TextInput
                        label="Phone number"
                        withAsterisk
                        {...form.getInputProps('phone')}
                        onChange={(event) =>
                            form.setFieldValue('phone', formatPhoneInput(event.currentTarget.value))
                        }
                    />
                    <TextInput label="Email" {...form.getInputProps('email')} />

                    <Group justify="flex-end">
                        <Button type="button" variant="default" onClick={handleClose}>
                            Cancel
                        </Button>
                        <Button type="submit" loading={isSubmitting}>
                            {isEdit ? 'Save changes' : 'Create customer'}
                        </Button>
                    </Group>
                </Stack>
            </form>
        </Modal>
    )
}
